// banner.service.ts
import { Injectable } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { BehaviorSubject, Observable } from 'rxjs';
import { generateUUID } from 'src/app/shared/utils/uuid';

// banner.model.ts
export type BannerLevel = 'info' | 'success' | 'warning' | 'error';
export type CtaType = 'primary' | 'secondary' | 'link';

export interface BannerCta {
  type: CtaType;
  i18nKey?: string; // optional if you pass plain label
  label?: string; // fallback/explicit label
  icon?: IconProp; // optional icon
  action: string; // e.g. 'auth:login', 'page:refresh'
  meta?: any;
}

export interface BannerMessage {
  id: string;
  level: BannerLevel;
  message: string;
  details?: any; // optional details object (will be stringified)
  i18nParams?: Record<string, any>;
  ctAs?: BannerCta[]; // support multiple CTAs
  sticky?: boolean; // errors/warnings default sticky
  scope?: string; // replace existing banner with same scope
  createdAt: number;
  autoCloseMs?: number; // non-sticky info/success default 7s
  focusOnShow?: boolean; // for assertive banners
}

@Injectable({ providedIn: 'root' })
export class BannerService {
  private readonly _banners$ = new BehaviorSubject<BannerMessage[]>([]);
  private readonly timers = new Map<string, any>(); // id -> timeout

  /** Stream of current banners (latest-first) */
  readonly banners$: Observable<BannerMessage[]> =
    this._banners$.asObservable();

  /** Show a banner (returns its id) */
  show(partial: Omit<BannerMessage, 'id' | 'createdAt'>): string {
    const id = generateUUID();
    const banner: BannerMessage = {
      id,
      createdAt: Date.now(),
      focusOnShow:
        partial.level === 'error' || partial.level === 'warning'
          ? true
          : partial.focusOnShow,
      autoCloseMs: this.defaultAutoClose(
        partial.level,
        partial.sticky,
        partial.autoCloseMs,
      ),
      ...partial,
    };

    this._banners$.next(
      this.insertReplacingScope(banner, this._banners$.value),
    );

    if (banner.autoCloseMs && !banner.sticky) {
      const t = setTimeout(() => this.dismiss(id), banner.autoCloseMs);
      this.timers.set(id, t);
    }

    return id;
  }

  /** Helpers for convenience */
  info(
    message: string,
    extras: Partial<Omit<BannerMessage, 'id' | 'createdAt' | 'level'>> = {},
  ) {
    return this.show({ level: 'info', message, ...extras });
  }
  success(
    message: string,
    extras: Partial<Omit<BannerMessage, 'id' | 'createdAt' | 'level'>> = {},
  ) {
    return this.show({
      level: 'success',
      message: message,
      ...extras,
    });
  }
  warning(
    message: string,
    extras: Partial<Omit<BannerMessage, 'id' | 'createdAt' | 'level'>> = {},
  ) {
    return this.show({
      level: 'warning',
      message: message,
      sticky: true,
      ...extras,
    });
  }
  error(
    message: string,
    extras: Partial<Omit<BannerMessage, 'id' | 'createdAt' | 'level'>> = {},
  ) {
    return this.show({
      level: 'error',
      message: message,
      sticky: true,
      ...extras,
    });
  }

  dismissAll(): void {
    this.clear();
  }
  /** Dismiss single banner */
  dismiss(id: string): void {
    const list = this._banners$.value.filter((b) => b.id !== id);
    this.clearTimer(id);
    this._banners$.next(list);
  }

  /** Clear all or by scope */
  clear(scope?: string): void {
    const list = scope
      ? this._banners$.value.filter((b) => b.scope !== scope)
      : [];
    // cleanup timers for removed items
    const removedIds = this._banners$.value
      .filter((b) => !list.some((nb) => nb.id === b.id))
      .map((b) => b.id);
    removedIds.forEach((id) => this.clearTimer(id));
    this._banners$.next(list);
  }

  // ---- internals ----
  private insertReplacingScope(
    banner: BannerMessage,
    current: BannerMessage[],
  ) {
    const filtered = banner.scope
      ? current.filter((b) => b.scope !== banner.scope)
      : current.slice();
    return [banner, ...filtered]; // newest first
  }

  private defaultAutoClose(
    level: BannerLevel,
    sticky?: boolean,
    override?: number,
  ) {
    if (typeof override === 'number') return override;
    if (sticky || level === 'error' || level === 'warning') return undefined;
    return 7000; // 7s for info/success
  }

  private clearTimer(id: string) {
    const t = this.timers.get(id);
    if (t) {
      clearTimeout(t);
      this.timers.delete(id);
    }
  }
}
